import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { FileText, Upload, Trash2 } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { formatDateTime, EmptyState } from "@/components/admin/ui";
import { LEAD_FILE_CATEGORY_LABELS } from "./salesMeta";

const btn = "inline-flex items-center gap-1.5 rounded-xl border border-transparent bg-[#0052FF] px-3.5 py-2 text-xs font-bold text-white transition-colors duration-150 hover:bg-[#0040CC] disabled:cursor-not-allowed disabled:opacity-50";

export const LeadFilesTab = ({ lead, intakeAttachments, onError, API }) => {
  const [files, setFiles] = useState(null);
  const [category, setCategory] = useState("photo");
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(() => {
    axios.get(`${API}/org/sales/leads/${lead.lead_id}/files`, { withCredentials: true })
      .then(({ data }) => setFiles(data.files))
      .catch((err) => onError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API, lead.lead_id, onError]);

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const form = new FormData();
    form.append("category", category);
    form.append("file", file);
    try {
      await axios.post(`${API}/org/sales/leads/${lead.lead_id}/files`, form, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      load();
    } catch (err) { onError(formatApiErrorDetail(err.response?.data?.detail)); } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const togglePublicSafe = async (fileId, value) => {
    try {
      await axios.patch(`${API}/org/sales/leads/${lead.lead_id}/files/${fileId}`, { is_public_safe: value }, { withCredentials: true });
      load();
    } catch (err) { onError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const removeFile = async (fileId) => {
    try {
      await axios.delete(`${API}/org/sales/leads/${lead.lead_id}/files/${fileId}`, { withCredentials: true });
      load();
    } catch (err) { onError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!files) return <p className="text-sm text-slate-400" data-testid="lead-files-loading">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="lead-files-tab">
      <div className="border-b border-slate-200 pb-5 dark:border-white/10">
        <p className="mb-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Intake Attachments</p>
        {intakeAttachments.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">No files were attached to the originating intake.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {intakeAttachments.map((a) => (
              <a key={a.index} href={`${API}/org/sales/leads/${lead.lead_id}/attachments/${a.index}`} target="_blank" rel="noreferrer" data-testid={`lead-intake-file-${a.index}`} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/10">
                File {a.index + 1} (from Intake)
              </a>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="mb-1 block text-xs font-bold uppercase tracking-[0.1em] text-slate-400">Category</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="lead-file-category-select" className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
            {Object.entries(LEAD_FILE_CATEGORY_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <label className={btn} data-testid="lead-file-upload-btn">
          <Upload size={13} /> {busy ? "Uploading…" : "Add Files"}
          <input ref={fileInputRef} type="file" onChange={upload} disabled={busy} className="hidden" accept="image/png,image/jpeg,image/webp,application/pdf" />
        </label>
      </div>

      {files.length === 0 ? (
        <EmptyState icon={FileText} title="No files yet" message="Add photos, documents, measurements, plans, or other files related to this lead or assessment." testId="lead-files-empty" />
      ) : (
        <div className="space-y-2" data-testid="lead-files-list">
          {files.map((f) => (
            <div key={f.file_id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3 dark:border-white/10" data-testid={`lead-file-item-${f.file_id}`}>
              <a href={`${API}/org/sales/leads/${lead.lead_id}/files/${f.file_id}/download`} target="_blank" rel="noreferrer" className="flex min-w-0 flex-1 items-center gap-2.5 hover:underline">
                <FileText size={16} className="shrink-0 text-slate-400" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-slate-900 dark:text-white">{f.original_filename}</p>
                  <p className="text-xs text-slate-400">{LEAD_FILE_CATEGORY_LABELS[f.category]} · {f.uploaded_by_name || "Unknown"} · {formatDateTime(f.created_at)}</p>
                </div>
              </a>
              <label className="flex shrink-0 items-center gap-1.5 text-[11px] font-bold text-slate-500 dark:text-slate-400">
                <input type="checkbox" checked={f.is_public_safe} onChange={(e) => togglePublicSafe(f.file_id, e.target.checked)} data-testid={`lead-file-public-safe-${f.file_id}`} />
                Proposal-Safe
              </label>
              <button type="button" onClick={() => removeFile(f.file_id)} data-testid={`lead-file-remove-${f.file_id}`} className="shrink-0 text-slate-400 hover:text-red-600 dark:hover:text-red-400">
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
